class CustomVideo extends HTMLElement {
  /** @type {HTMLVideoElement | null} */
  #video = this.querySelector('video');
  /** @type {HTMLButtonElement | null} */
  #button = this.querySelector('button[data-play-pause-button]') ?? this.querySelector('button');
  /** @type {HTMLButtonElement | null} */
  #soundButton = this.querySelector('button[data-sound-button]');
  /** @type {boolean} */
  #lazy = this.hasAttribute('data-lazy');
  /** @type {boolean} */
  #autoplay = this.hasAttribute('data-autoplay');
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;
  /** @type {boolean} */
  #userInteracted = false;

  connectedCallback() {
    if (!this.#video) return;

    this.#handleReducedMotion();

    if (this.#lazy) {
      this.#intersectionObserver = new IntersectionObserver(this.#handleIntersection.bind(this));
      this.#intersectionObserver.observe(this.#video);
    }

    if (this.#button) {
      this.#button.addEventListener('click', this.#handleClick.bind(this));
    }

    if (this.#soundButton) {
      this.#soundButton.addEventListener('click', this.#handleSoundClick.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    if (this.#button) {
      this.#button.removeEventListener('click', this.#handleClick.bind(this));
    }
  }

  /**
   * Handle intersection of the video element.
   * @param {IntersectionObserverEntry[]} entries - The intersection observer entries.
   * @returns {void}
   */
  #handleIntersection(entries) {
    entries.forEach((entry) => {
      // Only play/pause video if user has interacted with it or if autoplay is enabled.
      const isToggleNeeded = this.#userInteracted || this.#autoplay;
      if (!isToggleNeeded) return;

      entry.isIntersecting ? this.#video.play() : this.#video.pause();
      this.#toggleButton();
    });
  }

  /**
   * Handle reduced motion preference by disabling autoplay and pausing video.
   * @returns {void}
   */
  #handleReducedMotion() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches && this.#autoplay) {
      this.#autoplay = false;
    }
  }

  /**
   * Handle button click to toggle video play/pause state.
   * @returns {void}
   */
  #handleClick() {
    this.#video.paused ? this.#video.play() : this.#video.pause();
    this.#userInteracted = true;
    this.#toggleButton();

    // if video is playing, show sound button
    this.#soundButton?.classList.toggle('hidden', this.#video.paused);
  }

  /**
   * Toggle visibility of a pair of icons: one shown when condition is true, the other when false.
   * @param {HTMLElement | null} container - Element containing the two icon elements.
   * @param {string} selectorFirst - Selector for the icon to show when showFirst is true.
   * @param {string} selectorSecond - Selector for the icon to show when showFirst is false.
   * @param {boolean} showFirst - When true, first icon visible and second hidden; when false, opposite.
   * @returns {void}
   */
  #toggleIconPair(container, selectorFirst, selectorSecond, showFirst) {
    const first = container?.querySelector(selectorFirst);
    const second = container?.querySelector(selectorSecond);
    first?.classList.toggle('hidden', !showFirst);
    second?.classList.toggle('hidden', showFirst);
  }

  /** Toggle visibility of play and pause icons based on video state. */
  #toggleButton() {
    this.#toggleIconPair(
      this.#button,
      '[data-icon="regular-play"]',
      '[data-icon="regular-pause"]',
      this.#video.paused
    );
  }

  #handleSoundClick() {
    this.#video.muted = !this.#video.muted;
    this.#toggleSoundButton();
  }

  /** Toggle visibility of volume and volume-off icons based on muted state. */
  #toggleSoundButton() {
    this.#toggleIconPair(
      this.#soundButton,
      '[data-icon="regular-volume"]',
      '[data-icon="regular-volume-off"]',
      !this.#video.muted
    );
  }
}
customElements.define('custom-video', CustomVideo);
